import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  Fragment,
  useRef,
} from 'react';

const ModalStackContext = createContext();

export function ModalStackProvider({ children }) {
  const [stack, setStack] = useState([]);
  const idRef = useRef(0);
  const popLockRef = useRef(false);

  const pushModal = useCallback((Component, propsOrFn = {}) => {
    const id = ++idRef.current;
    setStack(prev => [...prev, { id, Component, propsOrFn }]);
    return id;
  }, []);

  const popModal = useCallback(() => {
    if (popLockRef.current) return;

    popLockRef.current = true;

    // When closing the modal we disable interactions for 1 second
    document.body.classList.add('no-interaction');
    setTimeout(() => {
      document.body.classList.remove('no-interaction');
    }, 1000); // After 1 sec we re-enable interactions

    // Schedule unlock in next event loop tick
    setTimeout(() => {
      popLockRef.current = false;
    }, 1000);

    setStack(prev => prev.slice(0, -1));
  }, []);

  const updateModal = useCallback((id, newPropsOrFn) => {
    setStack(prev =>
      prev.map(modal => {
        if (modal.id !== id) return modal;

        const currentProps =
          typeof modal.propsOrFn === 'function'
            ? modal.propsOrFn()
            : modal.propsOrFn;

        const newProps =
          typeof newPropsOrFn === 'function'
            ? newPropsOrFn(currentProps)
            : newPropsOrFn;

        return {
          ...modal,
          propsOrFn: () => ({ ...currentProps, ...newProps }),
        };
      })
    );
  }, []);

  // Escape key to close top modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && stack.length > 0) {
        popModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [stack.length, popModal]);

  return (
    <ModalStackContext.Provider value={{ pushModal, popModal, updateModal, stack }}>
      {children}

      {stack.map(({ id, Component, propsOrFn }, index) => {
        const zIndex = 1000 + index * 10;
        const props = typeof propsOrFn === 'function' ? propsOrFn() : propsOrFn;

        return (
          <Fragment key={id}>
            <Component {...props} zIndex={zIndex} onClose={popModal} />
          </Fragment>
        );
      })}
    </ModalStackContext.Provider>
  );
}

export function useModalStack() {
  return useContext(ModalStackContext);
}
